import { StackShim as Stack, StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import React from 'react';
import {
  partitionAvatarGroupItems,
  AvatarGroup,
  AvatarGroupItem,
  AvatarGroupPopover,
  Avatar,
  Label,
  makeStyles,
} from '@fluentui/react-components';
import {
  labelStyles,
  processTemplateMilestoneRulesSpan,
  AvatarGroupStyles,
} from '../../../../app/common/styles/CommonStyleObjects';
import {
  IProcessTemplateMilestone,
  IProcessTemplateMilestoneRule,
} from '../../../../utils/types/IProcessTemplate';
import SpanWithLabel from '../../../common/SpanWithLabel';
import { MilestoneRuleType } from '../../../companies/processes/Interfaces';
import ProcessTemplateMilestoneDrilldownInfo from './ProcessTemplateMilestoneDrilldownInfo';

interface IProcessTemplateMilestoneContentProps {
  milestone: IProcessTemplateMilestone;
}

const useContentStyles = makeStyles({
  roleColumn: { flex: '0 0 15%' },
});

const ProcessTemplateMilestoneContent = (
  props: IProcessTemplateMilestoneContentProps,
): JSX.Element => {
  const { milestone } = props;
  const labelClasses = labelStyles();
  const styles = AvatarGroupStyles();
  const contentClasses = useContentStyles();

  const personas =
    milestone.signoffRoles?.map((signoffRole, index) => ({
      id: index.toString(),
      name: signoffRole.name,
    })) ?? [];
  const { inlineItems, overflowItems } = partitionAvatarGroupItems({
    items: personas,
    maxInlineItems: 5,
  });

  const renderRoleAssignment = (): JSX.Element => {
    return (
      <div className={styles.assigneeContainer}>
        <Label className={labelClasses.root}>Sign Off Role(s)</Label>
        {personas.length > 0 && (
          <AvatarGroup className={styles.avatarGroup}>
            {inlineItems.map((item) => (
              <AvatarGroupItem name={item.name} key={item.id} />
            ))}
            {(overflowItems?.length ?? 0) > 0 && (
              <AvatarGroupPopover>
                {overflowItems.map((item) => (
                  <AvatarGroupItem name={item.name} key={item.id} />
                ))}
              </AvatarGroupPopover>
            )}
          </AvatarGroup>
        )}
        {personas.length === 0 && (
          <Avatar className={styles.avatarGroup} initials="NA" name="Not Assigned" />
        )}
      </div>
    );
  };

  const renderDurationLabels = (duration: number) => {
    const durationString = duration > 0 ? duration.toString() : '--';
    return (
      <Stack horizontal verticalAlign="baseline">
        <SpanWithLabel labelText="Duration" value={durationString} />
      </Stack>
    );
  };

  const lockdownModeText = (lockdownType: string) => {
    return `No edits to ${lockdownType} is allowed after this milestone has been signed off`;
  };

  const renderMilestoneRulesList = (milestoneRule: IProcessTemplateMilestoneRule): JSX.Element => {
    let milestoneRulesString = '';
    switch (milestoneRule.type) {
      case MilestoneRuleType.reasonForViolation:
        milestoneRulesString = 'Requires reason for filing violation';
        break;
      case MilestoneRuleType.documentUpload:
        milestoneRulesString = 'Requires upload documents';
        break;
      case MilestoneRuleType.dividendGate:
        milestoneRulesString = 'Requires dividend information';
        break;
      case MilestoneRuleType.auditOpinion:
        milestoneRulesString = 'Requires audit opinion information';
        break;
      case MilestoneRuleType.celaAgmStatus:
        milestoneRulesString = 'Requires CELA AGM Status';
        break;
      case MilestoneRuleType.agmDetail:
        milestoneRulesString = 'Requires AGM review';
        break;
      case MilestoneRuleType.agmDetailLockdownMode:
        milestoneRulesString = lockdownModeText('AGM review');
        break;
      case MilestoneRuleType.dividendLockdownMode:
        milestoneRulesString = lockdownModeText('dividends');
        break;
      case MilestoneRuleType.celaAgmStatusLockdownMode:
        milestoneRulesString = lockdownModeText('the CELA AGM status');
        break;
      case MilestoneRuleType.auditOpinionLockdownMode:
        milestoneRulesString = lockdownModeText('the audit opinion');
        break;
      default:
        milestoneRulesString = milestoneRule.type;
        break;
    }
    return (
      <li key={milestoneRule.type} style={{ listStyleType: 'none', marginBottom: 20 }}>
        <span className={processTemplateMilestoneRulesSpan}>{milestoneRulesString}</span>
      </li>
    );
  };
  return (
    <Stack>
      <Stack horizontal verticalAlign="baseline" tokens={{ childrenGap: 30 }} wrap>
        <StackItem className={contentClasses.roleColumn}>{renderRoleAssignment()}</StackItem>
        <StackItem>{renderDurationLabels(milestone.duration)}</StackItem>
        <div>
          <SpanWithLabel labelText="Category" value={milestone.milestoneCategoryName} />
        </div>
        {milestone.milestoneRules && milestone.milestoneRules.length > 0 && (
          <Stack grow={1}>
            <StackItem align="end">
              <ul>
                {milestone.milestoneRules.map((milestoneRule: IProcessTemplateMilestoneRule) => {
                  return renderMilestoneRulesList(milestoneRule);
                })}
              </ul>
            </StackItem>
          </Stack>
        )}
      </Stack>
      <StackItem>
        <ProcessTemplateMilestoneDrilldownInfo milestone={milestone} />
      </StackItem>
    </Stack>
  );
};
export default ProcessTemplateMilestoneContent;
